import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { Plus, Pencil, Trash2, CheckCircle, AlertCircle, X, Warehouse } from "lucide-react";

const TYPES = ["owned", "3PL", "bonded", "cold-chain", "cross-dock"];

const EMPTY_FORM = { name: "", type: "owned", address: "", country: "Australia", capacity: "" };

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/warehouses");
      setWarehouses(res.data.warehouses || []);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setModal("create"); };
  const openEdit = (w) => {
    setSelected(w);
    setForm({ name: w.name || "", type: w.type || "owned", address: w.address || "", country: w.country || "Australia", capacity: w.capacity || "" });
    setModal("edit");
  };
  const openDelete = (w) => { setSelected(w); setModal("delete"); };
  const closeModal = () => { setModal(null); setSelected(null); setError(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { flash("Warehouse name is required", "error"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        await api.post("/api/warehouses", form);
        flash("Warehouse created.");
      } else {
        await api.put(`/api/warehouses/${selected.id}`, form);
        flash("Warehouse updated.");
      }
      closeModal();
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/warehouses/${selected.id}`);
      flash("Warehouse deleted.");
      closeModal();
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (w) => {
    try {
      await api.put(`/api/warehouses/${w.id}`, { active: !w.active });
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const TYPE_COLORS = {
    owned:       "bg-blue-100 text-blue-700",
    "3PL":       "bg-violet-100 text-violet-700",
    bonded:      "bg-amber-100 text-amber-700",
    "cold-chain":"bg-cyan-100 text-cyan-700",
    "cross-dock":"bg-orange-100 text-orange-700",
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-xl"><Warehouse size={24} className="text-amber-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Warehouses</h1>
            <p className="text-slate-500 text-sm">Manage storage locations for your operations</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors">
          <Plus size={16}/> Add Warehouse
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="col-span-3 text-center py-16 text-slate-400 text-sm">Loading warehouses...</p>
        ) : warehouses.length === 0 ? (
          <p className="col-span-3 text-center py-16 text-slate-400 text-sm">No warehouses yet. Add your first one.</p>
        ) : warehouses.map(w => (
          <div key={w.id} className={`bg-white rounded-2xl border shadow-sm p-5 transition-all ${w.active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-bold text-navy-900">{w.name}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mt-1 inline-block ${TYPE_COLORS[w.type] || "bg-slate-100 text-slate-500"}`}>
                  {w.type}
                </span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(w)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600"><Pencil size={14}/></button>
                <button onClick={() => openDelete(w)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 size={14}/></button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-1">{w.address || "No address"}</p>
            <p className="text-xs text-slate-400">{w.country}</p>
            {w.capacity && <p className="text-xs text-slate-400 mt-1">Capacity: {w.capacity} units</p>}
            <div className="mt-4 flex items-center justify-between">
              <span className={`text-xs font-semibold ${w.active ? "text-emerald-600" : "text-slate-400"}`}>
                {w.active ? "Active" : "Inactive"}
              </span>
              <button onClick={() => handleToggle(w)}
                className={`text-xs px-3 py-1 rounded-lg font-semibold transition-colors ${w.active ? "bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                {w.active ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {(modal === "create" || modal === "edit") && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="font-bold text-navy-900 text-lg">{modal === "create" ? "Add Warehouse" : "Edit Warehouse"}</h2>
              <button onClick={closeModal}><X size={18} className="text-slate-400"/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {error && <p className="text-xs text-red-500">{error}</p>}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  placeholder="e.g. Sydney Warehouse"
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Capacity (units)</label>
                  <input type="number" value={form.capacity} onChange={e => setForm(f => ({...f, capacity: e.target.value}))}
                    placeholder="Optional"
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Address</label>
                <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))}
                  placeholder="123 Street, City, State"
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Country</label>
                <input value={form.country} onChange={e => setForm(f => ({...f, country: e.target.value}))}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"/>
              </div>
            </div>
            <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={closeModal} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-navy-900 text-white text-sm font-bold hover:bg-navy-800 disabled:opacity-50">
                {saving ? "Saving..." : modal === "create" ? "Add Warehouse" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="font-bold text-navy-900 text-lg mb-2">Delete Warehouse</h2>
            <p className="text-sm text-slate-500 mb-6">Delete <span className="font-semibold">{selected.name}</span>? This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={closeModal} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50">
                {saving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
